import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Grid, useMediaQuery, useTheme } from '@mui/material'

import {
  LayoutFormat,
  LayoutButtons,
  Filter,
  PaginationButtons,
  PaginationContext,
  Loader,
} from 'src/components'
import { initialScanDraftsFilter, usePagination, useScanDrafts } from 'src/hooks'

import { countOffset, sortToObject } from 'src/common/utils'
import { defaultPage, defaultLimit, defaultSort, sortOptions } from './filter'
import { useFilter } from 'src/hooks/common/use-filter.hook'
import { ScanDraftsTable } from './table'
import { ScanDraftsGrid } from './grid'
import { ETranslationNs } from 'src/types'

export const ScanDrafts = () => {
  const [t] = useTranslation(ETranslationNs.Scans)
  const [filter, setFilter] = useFilter({ initialState: initialScanDraftsFilter })
  const [sort, setSort] = useState(defaultSort)
  const { page, setPage, rowsPerPage, setRowsPerPage } = usePagination(defaultPage, defaultLimit)
  const theme = useTheme()
  const isLaptop = useMediaQuery(theme.breakpoints.down('laptop_l'))
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'))

  const [layoutFormat, setLayoutFormat] = useState<LayoutFormat>(
    isLaptop ? LayoutFormat.Grid : LayoutFormat.Table,
  )
  const {
    data,
    refetch: refetchScanDrafts,
    isLoading,
  } = useScanDrafts({
    ...filter,
    offset: countOffset(page, rowsPerPage),
    limit: rowsPerPage,
    ...sortToObject(sort),
  })

  useEffect(() => {
    refetchScanDrafts()
  }, [filter, sort, page, rowsPerPage, refetchScanDrafts])

  useEffect(() => {
    setPage(defaultPage)
  }, [filter, setPage, sort])

  const handleLayoutChange = () => setLayoutFormat(layoutFormat > 0 ? 0 : 1)

  const paginationValues = {
    count: data?.count ?? 0,
    page: page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    isShown: !!data && data.count > rowsPerPage,
  }

  return (
    <Grid container>
      <Filter
        sort={sort}
        setSort={setSort}
        searchFieldText={t('scan-drafts-search-input.v')}
        filter={filter}
        setFilter={setFilter}
        selectionOptions={[]}
        sortOptions={sortOptions}
        initialState={initialScanDraftsFilter}
      />
      <PaginationContext.Provider value={paginationValues}>
        <LayoutButtons layoutFormat={layoutFormat} handleLayoutChange={handleLayoutChange} />
        {isLoading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              m: '30px',
            }}
          >
            <Loader isStatic />
          </Box>
        )}
        {!isLoading &&
          (layoutFormat === LayoutFormat.Table ? (
            <ScanDraftsTable sort={sort} setSort={setSort} data={data?.data} />
          ) : (
            <ScanDraftsGrid data={data?.data} />
          ))}
        {!isTablet && <PaginationButtons />}
      </PaginationContext.Provider>
    </Grid>
  )
}
